import {
  FirestoreDataConverter,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot,
  SnapshotOptions,
} from "firebase/firestore";
import { GroceryItem, ListMetadata } from "../types/grocery";
import { FALLBACK_CATEGORY, normalizeStandardCategoryKey } from "../constants";

const CANONICAL_GROCERY_ITEM_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u;

export const groceryItemConverter: FirestoreDataConverter<GroceryItem> = {
  toFirestore(item: GroceryItem): DocumentData {
    // Don't store id in the document data, it's the document name
    const {
      id: _id,
      createdAt,
      checkedAt,
      categorizationStatus: _categorizationStatus,
      ...rest
    } = item;
    return {
      ...rest,
      createdAt: Timestamp.fromDate(
        createdAt instanceof Date ? createdAt : new Date(createdAt),
      ),
      ...(checkedAt
        ? {
            checkedAt: Timestamp.fromDate(
              checkedAt instanceof Date ? checkedAt : new Date(checkedAt),
            ),
          }
        : {}),
    };
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions,
  ): GroceryItem {
    if (!CANONICAL_GROCERY_ITEM_ID.test(snapshot.id)) {
      throw new Error("Shared Grocery Item identity is incompatible.");
    }
    const data = snapshot.data(options);
    let category = data.category || FALLBACK_CATEGORY;
    // Normalize category: replace 'grocery_list.category.' with 'grocery.category.'
    if (
      typeof category === "string" &&
      category.startsWith("grocery_list.category.")
    ) {
      category = category.replace(
        "grocery_list.category.",
        "grocery.category.",
      );
    }
    category = normalizeStandardCategoryKey(category) || category;
    const sharedListId =
      typeof data.sharedListId === "string" ? data.sharedListId : undefined;
    const sharedListCreatedBy =
      typeof data.sharedListCreatedBy === "string"
        ? data.sharedListCreatedBy
        : undefined;
    const sharedListLastEditedBy =
      typeof data.sharedListLastEditedBy === "string"
        ? data.sharedListLastEditedBy
        : undefined;
    const categoryDisplayName =
      typeof data.categoryDisplayName === "string" &&
      data.categoryDisplayName.trim()
        ? data.categoryDisplayName.trim()
        : undefined;

    return {
      id: snapshot.id,
      name: data.name || "",
      category: category, // Use normalized category
      ...(categoryDisplayName ? { categoryDisplayName } : {}),
      isChecked: !!data.isChecked,
      ...(data.checkedAt
        ? {
            checkedAt:
              data.checkedAt instanceof Timestamp
                ? data.checkedAt.toDate()
                : new Date(data.checkedAt),
          }
        : {}),
      createdAt:
        data.createdAt instanceof Timestamp
          ? data.createdAt.toDate()
          : new Date(data.createdAt),
      ...(sharedListId ? { sharedListId } : {}),
      ...(sharedListCreatedBy ? { sharedListCreatedBy } : {}),
      ...(sharedListLastEditedBy ? { sharedListLastEditedBy } : {}),
      updatedViaSharing:
        data.updatedViaSharing === undefined
          ? undefined
          : !!data.updatedViaSharing,
    } as GroceryItem;
  },
};

export const sharedListMetadataConverter: FirestoreDataConverter<ListMetadata> =
  {
    toFirestore(metadata: ListMetadata): DocumentData {
      return {
        metadata: {
          ...metadata,
        },
      };
    },
    fromFirestore(
      snapshot: QueryDocumentSnapshot,
      options: SnapshotOptions,
    ): ListMetadata {
      const data = snapshot.data(options);
      const meta = data.metadata ? data.metadata : data;
      return {
        title: meta.title,
        created:
          meta.created instanceof Timestamp
            ? meta.created.toDate()
            : new Date(meta.created),
        expires: meta.expires
          ? meta.expires instanceof Timestamp
            ? meta.expires.toDate()
            : new Date(meta.expires)
          : undefined,
        accessConfig: meta.accessConfig
          ? meta.accessConfig
          : { allowEditing: true, requireAuth: false },
        isPermanent: !!meta.isPermanent,
      } as ListMetadata;
    },
  };
